import { forwardRef } from 'react';

import classNames from 'classnames';
import {
  ArrowUpRight,
  Bookmark as LucideBookmark,
  Check as LucideCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Code,
  Copy as LucideCopy,
  CornerDownLeft,
  EllipsisVertical,
  File,
  FileText,
  Film,
  GripVertical,
  Image,
  Info as LucideInfo,
  ListFilter,
  LoaderCircle,
  Lock as LucideLock,
  LucideIcon,
  Menu as LucideMenu,
  Minus as LucideMinus,
  Monitor as LucideMonitor,
  Moon as LucideMoon,
  Music2,
  Pencil as LucidePencil,
  Plus as LucidePlus,
  Search,
  Settings,
  Sun as LucideSun,
  Table2,
  User as LucideUser,
  X,
  Zap,
} from 'lucide-react';

import { ICON_STROKE_WIDTH } from './icons.constants';
import { IIconProps } from './icons.interface';
import styles from './icons.module.scss';

const ICON_SIZE = { small: 16, large: 24, lock: 40, spinner: 14 } as const;

// Keep the kit's established sizes and foreground roles while sharing Lucide's
// geometry, SVG props, accessibility defaults and ref forwarding.
const withDefaults = (
  Icon: LucideIcon,
  size: NonNullable<IIconProps['size']> = ICON_SIZE.small,
  color = 'currentColor',
  strokeWidth = ICON_STROKE_WIDTH,
): LucideIcon => {
  const Component = forwardRef<SVGSVGElement, IIconProps>(
    ({ size: iconSize = size, color: iconColor = color, ...props }, ref) => (
      <Icon
        size={iconSize}
        color={iconColor}
        focusable="false"
        {...props}
        strokeWidth={strokeWidth}
        nonScalingStroke
        absoluteStrokeWidth={false}
        ref={ref}
      />
    ),
  );
  Component.displayName = `Skiloope${Icon.displayName}`;
  return Component;
};

export const ArrowDown = withDefaults(
  ChevronDown,
  ICON_SIZE.large,
  'var(--sk-icon-default)',
);
export const ArrowLeft = withDefaults(ChevronLeft);
export const ArrowRight = withDefaults(ChevronRight);
export const ArrowTopRight = withDefaults(
  ArrowUpRight,
  ICON_SIZE.large,
  'var(--sk-icon-default)',
);
export const Attachment = withDefaults(File, ICON_SIZE.small, 'var(--sk-icon-default)');
export const Bookmark = withDefaults(
  LucideBookmark,
  ICON_SIZE.small,
  'var(--sk-icon-default)',
);
export const Check = withDefaults(LucideCheck);
const RoundCheck = withDefaults(CircleCheck);
export const CheckmarkRound = forwardRef<
  SVGSVGElement,
  IIconProps & { filled?: boolean }
>(({ filled = false, color, className, style, ...props }, ref) => (
  <RoundCheck
    {...props}
    ref={ref}
    color={color}
    className={classNames(className, { [styles.filled]: filled })}
    style={filled ? { ...style, color: color ?? 'currentColor' } : style}
  />
));
CheckmarkRound.displayName = 'SkiloopeCheckmarkRound';
export const Close = withDefaults(X);
export const CloseSmall = withDefaults(X, ICON_SIZE.small, 'var(--sk-icon-default)');
export const Cogwheel = withDefaults(Settings);
export const Lightning = withDefaults(Zap, ICON_SIZE.small, 'var(--sk-icon-default)');
export const Copy = withDefaults(LucideCopy, ICON_SIZE.large, 'var(--sk-icon-default)');
export const Enter = withDefaults(CornerDownLeft);
export const FiletypeAudio = withDefaults(Music2, ICON_SIZE.large);
export const FiletypeCode = withDefaults(Code, ICON_SIZE.large);
export const FiletypeDocument = withDefaults(FileText, ICON_SIZE.large);
export const FiletypeImage = withDefaults(Image, ICON_SIZE.large);
export const FiletypeSpreadsheet = withDefaults(Table2, ICON_SIZE.large);
export const FiletypeVideo = withDefaults(Film, ICON_SIZE.large);
export const Filter = withDefaults(ListFilter);
export const Info = withDefaults(LucideInfo, ICON_SIZE.small, 'var(--sk-icon-default)');
export const Lock = withDefaults(LucideLock, ICON_SIZE.lock, 'var(--sk-icon-default)');
export const MainMenu = withDefaults(
  LucideMenu,
  ICON_SIZE.large,
  'var(--sk-icon-default)',
);
export const Minus = withDefaults(LucideMinus);
export const Monitor = withDefaults(LucideMonitor);
export const Moon = withDefaults(LucideMoon);
export const Pencil = withDefaults(LucidePencil);
export const Plus = withDefaults(LucidePlus);
export const Reorder = withDefaults(GripVertical);
export const SearchIcon = withDefaults(Search);
export const Spinner = withDefaults(LoaderCircle, ICON_SIZE.spinner);
export const Sun = withDefaults(LucideSun);
export const ThreeDots = withDefaults(
  EllipsisVertical,
  ICON_SIZE.small,
  'currentColor',
  1,
);
export const User = withDefaults(LucideUser, ICON_SIZE.large, 'var(--sk-icon-subtle)');
export const Vector = withDefaults(
  ArrowUpRight,
  ICON_SIZE.small,
  'var(--sk-icon-default)',
);
